/**
 * Constructor docs indicating window object namespaces/keys relevant to beacon tracking
 * - "Logged in user ID" docs
 *     - https://docs.constructor.com/docs/integrating-with-constructor-behavioral-tracking-data-driven-event-tracking#logged-in-user-id
 * - "Order details / Purchase confirmation page" docs
 *     - https://docs.constructor.com/docs/integrating-with-constructor-behavioral-tracking-data-driven-event-tracking#order-details--purchase-confirmation-page
 */
const c9rNamespaceMap = {
  beaconTarget: 'cnstrc',
  loggedInUser: 'userId',
  orderDetails: 'purchaseData',
};

class ConstructorUtils {
  constructor(target) {
    this.target = target || window;
  }

  /**
   * Purchase data factory method
   * that guards for a single successful initialization
   * @param orderModel
   * @return {Object} | undefined
   */
  buildPurchaseData(orderModel) {
    if (!this.purchaseData) {
      const initData = initC9rPurchaseData(orderModel, this.target);
      if (initData) {
        this.purchaseData = initData;
      }
    }
    return this.purchaseData;
  }

  static get namespaceMap() {
    return c9rNamespaceMap;
  }
}

/**
 * Initializess a window object property
 * for Constructor (search platform vendor) beacon user behavioral event tracking
 * and adds a child node property that provides successful purchase data
 * per their "Order details / purchase confirmation page" documentation
 * - https://docs.constructor.com/docs/integrating-with-constructor-behavioral-tracking-data-driven-event-tracking#order-details--purchase-confirmation-page
 * from an order model only if line items exist.
 * Purchase data is returned on successful init otherwise no return value.
 *
 * @param orderModel {Object}
 * @param beaconTargetContext {Object}
 * @return {Object} | void
 */
const initC9rPurchaseData = (orderModel, beaconTargetContext = window) => {
  const {
    externalOrderId: order_id,
    lineItems,
    totalPrice,
  } = orderModel || {};
  if (!(Array.isArray(lineItems) && lineItems.length > 0)) {
    return;
  }
  /**
   * Original product(s) price total with any product(s) discounts applied but no taxes applied
   * Nolan Cross advised that this (AKA orderSubTotal) is a standard revenue analytics metric.
   * (circa 2025-01-21)
   *
   * Per Constructor "Order details / purchase confirmation page" docs:
   * > revenue should be a numeric value that represents either the purchase subtotal
   * > or the total amount including taxes, discounts, etc.
   * > It should match what is tracked within any relevant analytics platforms.
   */
  const { totalProductAdjusted: revenue } = totalPrice || {};

  let { [c9rNamespaceMap.beaconTarget]: c9rBeaconTarget } = beaconTargetContext;
  if (!c9rBeaconTarget) {
    // Init uninitialized target namespace/parent (races with other similar C9r beacon data)
    c9rBeaconTarget = {};
    beaconTargetContext[c9rNamespaceMap.beaconTarget] = c9rBeaconTarget;
  }
  const purchaseData = {
    items: (lineItems || []).map(buildC9rItem),
    order_id,
    revenue,
  }
  c9rBeaconTarget[c9rNamespaceMap.orderDetails] = purchaseData;
  return purchaseData;
};

/**
 * Transforms an OPW order line item into an object matching the Constructor tracking docs example
 * - https://docs.constructor.com/docs/integrating-with-constructor-behavioral-tracking-data-driven-event-tracking#order-details--purchase-confirmation-page
 * @param orderModelLineItem
 * @returns {{count: *, item_id: *, price: *, variation_id: *}}
 */
const buildC9rItem = (orderModelLineItem) => {
  const {
    price: priceObj,
    quantity: count,
    sku: variation_id,
  } = orderModelLineItem || {};
  const { unitAdjusted: price } = priceObj || {};

  /**
   * WARNING:
   * A 10 char sku id may eventually be decoupled from a 6 char style id
   * causing problems with `substring` technique to derive style from the initial sku chars.
   * This change related to alphanumeric id progress is mentioned here:
   * https://rei-coop.atlassian.net/wiki/spaces/MA/pages/2917205/Impacts+of+10+digit+SKU+no+longer+linked+to+6+digit+Style
   */
  const c9rItem = {
    count,
    price,
  };
  if (variation_id) {
    c9rItem.item_id = variation_id.substring(0, 6);
    c9rItem.variation_id = variation_id;
  }
  return c9rItem;
}

export default ConstructorUtils;
